import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Upload,
  Users,
  LayoutGrid,
  Send,
  Check,
  ChevronRight,
  ChevronLeft,
  FileText,
  Loader2,
  ZoomIn,
  ZoomOut,
  AlertCircle,
  X,
  Mail,
} from 'lucide-react';
import useEnvelopeDraft from '../hooks/useEnvelopeDraft';
import * as api from '../lib/esignApi';
import { useAuth } from '../hooks/useAuth';
import { pdfjsLib } from '../lib/pdfWorker';
import PageCanvas from '../components/PageCanvas';
import RecipientList, { RECIPIENT_COLORS } from '../components/esign/RecipientList';
import FieldPalette from '../components/esign/FieldPalette';
import FieldPlacer from '../components/esign/FieldPlacer';
import SignInRequired from '../components/SignInRequired';

// ─── Step definitions ────────────────────────────────────────────────────────

const STEPS = [
  { key: 'upload', label: 'Upload', Icon: Upload },
  { key: 'recipients', label: 'Recipients', Icon: Users },
  { key: 'fields', label: 'Place Fields', Icon: LayoutGrid },
  { key: 'review', label: 'Review & Send', Icon: Send },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function recipientLabel(r) {
  return r.name || r.email || 'Unnamed';
}

// "michael.jordan+test@example.com" → "Michael Jordan". Mirrors
// api/lib/esign/senderName.js so the frontend prefill matches what the
// backend would resolve to if the field is left empty — no surprise jump
// between what the sender types and what the recipient receives.
function deriveSenderFromEmail(email) {
  if (!email || typeof email !== 'string') return '';
  const at = email.indexOf('@');
  if (at <= 0) return '';
  const local = email.slice(0, at).split('+')[0];
  if (!local) return '';
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Truncates to 2 named signers, overflow rendered as "and N others".
function captionForZeroFieldSigners(list) {
  if (list.length === 0) return '';
  const names = list.map(recipientLabel);
  if (names.length === 1) return `${names[0]} has no fields yet`;
  if (names.length === 2) return `${names[0]} and ${names[1]} have no fields yet`;
  const rest = names.length - 1;
  return `${names[0]} and ${rest} other${rest === 1 ? '' : 's'} have no fields yet`;
}

// Full list with commas + "and" before the last name (screen readers get
// the complete roster via the disabled-button tooltip).
function tooltipForZeroFieldSigners(list) {
  if (list.length === 0) return '';
  const names = list.map(recipientLabel);
  const verb = names.length === 1 ? 'needs' : 'need';
  if (names.length === 1) return `${names[0]} ${verb} at least one required field`;
  if (names.length === 2) return `${names[0]} and ${names[1]} ${verb} at least one required field`;
  const head = names.slice(0, -1).join(', ');
  const last = names[names.length - 1];
  return `${head} and ${last} ${verb} at least one required field`;
}

function defaultExpiry() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().split('T')[0];
}

let fieldIdCounter = 0;
function nextFieldId() {
  fieldIdCounter += 1;
  return `local_${fieldIdCounter}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EsignCompose() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const draft = useEnvelopeDraft();
  const {
    envelope,
    recipients,
    fields,
    loading: draftLoading,
    error: draftError,
    syncError,
    setRecipients,
    setFields,
    createDraft,
    updateMetadata,
    send,
  } = draft;

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState('');
  const [routing, setRouting] = useState('parallel');
  const [message, setMessage] = useState('');
  const [expiry, setExpiry] = useState(defaultExpiry);
  const [reminderInterval, setReminderInterval] = useState(3);
  const [reminderMax, setReminderMax] = useState(3);
  // sender_name override + "save as default" toggle for the email From line.
  // Prefilled from prefs.sender_display_name or the derived account name in
  // the prefs-load effect below. Empty means "use the standard fallback
  // chain" — the backend resolves to prefs.sender_display_name then to
  // account.display_name / first+last / etc.
  const [senderName, setSenderName] = useState('');
  const [senderNameDefault, setSenderNameDefault] = useState(''); // initial prefill, used as fallback
  const [saveSenderAsDefault, setSaveSenderAsDefault] = useState(false);

  // Prefill from per-account defaults (non-fatal on failure).
  // Gated on auth readiness to avoid the "Not signed in" race on first paint.
  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { preferences } = await api.getEsignPreferences();
        if (cancelled || !preferences) return;
        const days = Number(preferences.default_expiration_days) || 30;
        const d = new Date();
        d.setDate(d.getDate() + days);
        setExpiry(d.toISOString().split('T')[0]);
        setReminderInterval(preferences.default_reminder_interval_days ?? 3);
        setReminderMax(preferences.default_reminder_max_count ?? 3);
        // Sender-name prefill chain mirrors the backend resolveEffectiveSenderName:
        //   1. prefs.sender_display_name (account-level eSign default)
        //   2. account.display_name (from profile)
        //   3. first + last
        //   4. first alone
        //   5. email-local-part title-cased (matches what the backend would do)
        const derived =
          preferences.sender_display_name?.trim() ||
          user.displayName?.trim() ||
          deriveSenderFromEmail(user.email) ||
          '';
        if (!cancelled) {
          setSenderName(derived);
          setSenderNameDefault(derived);
        }
      } catch (err) {
        console.warn('Failed to load eSign preferences:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user]);

  // Step 3 state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageInfos, setPageInfos] = useState([]); // [{ width, height }] for each page
  const [zoom, setZoom] = useState(1);
  const [activeRecipientId, setActiveRecipientId] = useState(null);
  const [activeFieldKind, setActiveFieldKind] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Step 4 state
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const fileInputRef = useRef(null);
  const pdfContainerRef = useRef(null);

  // ─── Step 1: Upload ──────────────────────────────────────────────────────

  const handleFileSelect = useCallback(
    async (selectedFile) => {
      if (!selectedFile || selectedFile.type !== 'application/pdf') return;
      setFile(selectedFile);
      const name = selectedFile.name.replace(/\.pdf$/i, '');
      setSubject(name);
      try {
        await createDraft(selectedFile, name);
      } catch {
        // Error handled by hook
      }
    },
    [createDraft],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      const dropped = e.dataTransfer.files[0];
      handleFileSelect(dropped);
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  // ─── Step 2: Recipients ──────────────────────────────────────────────────

  // Color-assign recipients
  const coloredRecipients = useMemo(
    () =>
      recipients.map((r, i) => ({
        ...r,
        color: RECIPIENT_COLORS[i % RECIPIENT_COLORS.length],
        id: r.id || `r_${i}`,
      })),
    [recipients],
  );

  const recipientColorMap = useMemo(() => {
    const m = {};
    coloredRecipients.forEach((r) => {
      m[r.id] = r.color;
    });
    return m;
  }, [coloredRecipients]);

  // Required-field counts per signer. Mirrors the server-side rule in
  // api/routes/envelopes.js (every signer needs ≥1 required field to send);
  // cc/approver are skipped because the server doesn't require fields for them.
  const signerFieldCounts = useMemo(() => {
    const counts = {};
    for (const r of coloredRecipients) {
      if (r.role !== 'signer') continue;
      counts[r.id] = fields.filter((f) => f.recipient_id === r.id && f.required).length;
    }
    return counts;
  }, [coloredRecipients, fields]);

  const signersWithoutFields = useMemo(
    () => coloredRecipients.filter(
      (r) => r.role === 'signer' && (signerFieldCounts[r.id] || 0) === 0,
    ),
    [coloredRecipients, signerFieldCounts],
  );

  // ─── Step 3: Load PDF for field placement ────────────────────────────────

  useEffect(() => {
    if (step !== 2 || !envelope?.id) return;
    let cancelled = false;

    async function loadPdf() {
      setPdfLoading(true);
      try {
        const buf = await api.getEnvelopeDocument(envelope.id);
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;
        setPdfDoc(doc);

        // Gather page dimensions
        const infos = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const vp = page.getViewport({ scale: 1 });
          infos.push({ width: vp.width, height: vp.height });
        }
        if (!cancelled) setPageInfos(infos);
      } catch (err) {
        console.error('Failed to load PDF for field placement:', err);
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [step, envelope?.id]);

  // Default active recipient: on first entry to step 3, pick the first signer
  // with zero required fields so placement flows through them naturally.
  // Later visits stay sticky (guarded by !activeRecipientId) — the auto-pick
  // only fires when nothing is selected, e.g. after removing the active
  // recipient on step 4 or on initial mount.
  useEffect(() => {
    if (step !== 2 || coloredRecipients.length === 0 || activeRecipientId) return;
    const firstZeroFieldSigner = coloredRecipients.find(
      (r) => r.role === 'signer' && (signerFieldCounts[r.id] || 0) === 0,
    );
    const fallback = coloredRecipients.find((r) => r.role === 'signer') || coloredRecipients[0];
    setActiveRecipientId((firstZeroFieldSigner || fallback).id);
  }, [step, coloredRecipients, activeRecipientId, signerFieldCounts]);

  // Field CRUD for Step 3
  const handleAddField = useCallback(
    (fieldData) => {
      setFields([...fields, { ...fieldData, id: nextFieldId() }]);
    },
    [fields, setFields],
  );

  const handleRemoveField = useCallback(
    (fieldId) => {
      setFields(fields.filter((f) => f.id !== fieldId));
    },
    [fields, setFields],
  );

  const handleUpdateField = useCallback(
    (fieldId, updates) => {
      setFields(fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)));
    },
    [fields, setFields],
  );

  // Remove a recipient and cascade-drop their fields. Confirms only when
  // fields are attached (silent removal otherwise). Refuses to drop the last
  // remaining recipient so the compose flow never ends up empty — callers
  // that render the button should still hide it in that case for clarity.
  const handleRemoveRecipient = useCallback(
    (recipientId) => {
      if (recipients.length <= 1) return;
      const target = coloredRecipients.find((r) => r.id === recipientId);
      if (!target) return;
      const theirFields = fields.filter((f) => f.recipient_id === recipientId);
      if (theirFields.length > 0) {
        const label = target.name || target.email || 'this recipient';
        const ok = window.confirm(
          `Remove ${label}? Their ${theirFields.length} field${theirFields.length === 1 ? '' : 's'} will also be removed.`,
        );
        if (!ok) return;
        setFields(fields.filter((f) => f.recipient_id !== recipientId));
      }
      setRecipients(recipients.filter((r) => r.id !== recipientId));
      if (activeRecipientId === recipientId) {
        setActiveRecipientId(null);
      }
    },
    [recipients, coloredRecipients, fields, setFields, setRecipients, activeRecipientId],
  );

  // ─── Step 4: Send ────────────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    setSending(true);
    setSendError(null);
    try {
      // sender_name handling: trim before PATCH. An empty string means
      // "use the standard fallback chain on the server" — pass null so
      // the backend clears any prior override on the draft.
      const trimmedSender = senderName.trim();
      const patch = {
        subject,
        message: message || undefined,
        expires_at: expiry || undefined,
        reminder_interval_days: Number(reminderInterval),
        reminder_max_count: Number(reminderMax),
        sender_name: trimmedSender === '' ? null : trimmedSender,
      };
      // Only attach save_as_default when the user actually ticked it AND
      // the value isn't blank — the PATCH route's side-effect needs both.
      if (saveSenderAsDefault && trimmedSender !== '') {
        patch.save_as_default = true;
      }
      await updateMetadata(patch);
      await send();
      navigate(`/esign/${envelope.id}`);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  }, [subject, message, expiry, reminderInterval, reminderMax, senderName, saveSenderAsDefault, updateMetadata, send, navigate, envelope?.id]);

  // ─── Validation ──────────────────────────────────────────────────────────

  const canNext = useMemo(() => {
    switch (step) {
      case 0:
        return !!envelope && !draftLoading;
      case 1: {
        const hasSigners = recipients.some((r) => r.role === 'signer');
        const allValid = recipients.every(
          (r) => r.name.trim() && isValidEmail(r.email),
        );
        return hasSigners && allValid && recipients.length > 0;
      }
      case 2:
        return true; // Fields are optional technically, but recommended
      case 3:
        return !sending;
      default:
        return false;
    }
  }, [step, envelope, draftLoading, recipients, sending]);

  // Logged-out (e.g. a licensed desktop user who signed out): eSign needs a
  // live session — prompt sign-in instead of a dead page.
  if (!authLoading && !user) {
    return <SignInRequired feature="eSign" />;
  }

  // ─── Stepper UI ──────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Step indicator */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          {STEPS.map((s, i) => {
            const isComplete = i < step;
            const isCurrent = i === step;
            return (
              <div key={s.key} className="flex items-center gap-2 flex-1 last:flex-initial">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition
                      ${
                        isComplete
                          ? 'bg-indigo-600 text-white'
                          : isCurrent
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                  >
                    {isComplete ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span
                    className={`text-sm font-medium hidden sm:inline ${
                      isCurrent ? 'text-gray-900' : 'text-gray-500'
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-px mx-2 ${
                      i < step ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {syncError && (
        <div role="status" className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {syncError}
        </div>
      )}

      {/* Step content */}
      <div className="flex-1 overflow-auto">
        {/* ── Step 1: Upload ─────────────────────────────────────────── */}
        {step === 0 && (
          <div className="p-8 max-w-2xl mx-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Upload your document</h2>

            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-xl p-12 text-center transition cursor-pointer ${
                file
                  ? 'border-indigo-300 bg-indigo-50'
                  : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files[0])}
              />
              {draftLoading ? (
                <Loader2 className="w-10 h-10 text-indigo-500 mx-auto mb-3 animate-spin" />
              ) : file ? (
                <FileText className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
              ) : (
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              )}

              {file ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  {envelope && (
                    <p className="text-xs text-gray-500 mt-1">
                      {envelope.page_count} page{envelope.page_count !== 1 ? 's' : ''} uploaded
                    </p>
                  )}
                  <p className="text-xs text-indigo-600 mt-2">Click to replace</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Drag a PDF here or click to browse
                  </p>
                  <p className="text-xs text-gray-400 mt-1">PDF files only</p>
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Employment Agreement"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {draftError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {draftError}
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Recipients ─────────────────────────────────────── */}
        {step === 1 && (
          <div className="p-8 max-w-2xl mx-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Add recipients</h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <RecipientList
                recipients={recipients}
                onChange={setRecipients}
                routing={routing}
                onRoutingChange={setRouting}
              />
            </div>

            {recipients.length === 0 && (
              <p className="text-sm text-gray-500 mt-4 text-center">
                Add at least one signer to continue.
              </p>
            )}
          </div>
        )}

        {/* ── Step 3: Place Fields ───────────────────────────────────── */}
        {step === 2 && (
          <div className="flex h-full min-h-0">
            {/* PDF viewer area */}
            <div className="flex-1 min-w-0 flex flex-col">
              {/* Zoom controls */}
              <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"
                  title="Zoom out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-500 w-12 text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition"
                  title="Zoom in"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>

              {/* Pages */}
              <div ref={pdfContainerRef} className="flex-1 overflow-auto bg-gray-100 p-6">
                {pdfLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                  </div>
                ) : pdfDoc ? (
                  <div className="space-y-4 flex flex-col items-center">
                    {pageInfos.map((info, idx) => {
                      const pageNum = idx + 1;
                      const pageFields = fields.filter(
                        (f) => f.page_num === pageNum,
                      );
                      return (
                        <div key={pageNum} className="relative shadow-lg">
                          <PageCanvas
                            pdfDoc={pdfDoc}
                            pageNumber={pageNum}
                            scale={zoom}
                            className="bg-white"
                          />
                          <FieldPlacer
                            pageNum={pageNum}
                            pageWidth={info.width}
                            pageHeight={info.height}
                            zoom={zoom}
                            fields={pageFields}
                            activeRecipientId={activeRecipientId}
                            activeFieldKind={activeFieldKind}
                            onAddField={handleAddField}
                            onRemoveField={handleRemoveField}
                            onUpdateField={handleUpdateField}
                            recipientColors={recipientColorMap}
                          />
                          {/* Page label */}
                          <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs text-gray-400">
                            Page {pageNum}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-sm text-gray-500">
                    Unable to load document.
                  </div>
                )}
              </div>
            </div>

            {/* Field palette (right sidebar) */}
            <div className="w-64 shrink-0 bg-white border-l border-gray-200 p-4 overflow-auto">
              <FieldPalette
                recipients={coloredRecipients}
                activeRecipientId={activeRecipientId}
                onRecipientChange={setActiveRecipientId}
                activeFieldKind={activeFieldKind}
                onFieldKindChange={setActiveFieldKind}
                signerFieldCounts={signerFieldCounts}
              />
            </div>
          </div>
        )}

        {/* ── Step 4: Review & Send ──────────────────────────────────── */}
        {step === 3 && (
          <div className="p-8 max-w-2xl mx-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Review & send</h2>

            {/* Document summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Document
              </h3>
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-indigo-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {file?.name || 'Uploaded document'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {envelope?.page_count || 1} page{(envelope?.page_count || 1) !== 1 ? 's' : ''} &middot;{' '}
                    {fields.length} field{fields.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Recipients summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Recipients
              </h3>
              <div className="space-y-2">
                {coloredRecipients.map((r) => {
                  const rFields = fields.filter((f) => f.recipient_id === r.id);
                  const kinds = [...new Set(rFields.map((f) => f.kind))];
                  const requiredCount = rFields.filter((f) => f.required).length;
                  const isZeroFieldSigner = r.role === 'signer' && requiredCount === 0;
                  const canRemove = coloredRecipients.length > 1;
                  const removeLabel = r.name || r.email || 'recipient';
                  return (
                    <div key={r.id} className="flex items-center justify-between text-sm gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: r.color }}
                        />
                        <span className="font-medium text-gray-900 truncate">{r.name || r.email}</span>
                        <span className="text-xs text-gray-400 capitalize shrink-0">({r.role})</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className={`text-xs ${isZeroFieldSigner ? 'text-amber-700' : 'text-gray-500'}`}
                        >
                          {rFields.length > 0
                            ? `${rFields.length} field${rFields.length !== 1 ? 's' : ''} (${kinds.join(', ')})`
                            : 'No fields yet'}
                        </span>
                        {canRemove && (
                          <button
                            type="button"
                            onClick={() => handleRemoveRecipient(r.id)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                            title={`Remove ${removeLabel}`}
                            aria-label={`Remove ${removeLabel}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Email preview — sender_name override + live subject preview.
                Lives above subject because the recipient sees the sender
                name first ("Apex Realty has asked you to sign..."). */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-indigo-500" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Email preview
                </h3>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 mb-4 text-sm text-gray-700 font-mono">
                <span className="text-gray-500">Subject: </span>
                <span className="text-gray-900">
                  {(senderName.trim() || senderNameDefault || 'Your name')}
                  {' has asked you to sign '}
                  <span className="text-gray-500">&quot;</span>
                  {subject || 'Untitled'}
                  <span className="text-gray-500">&quot;</span>
                </span>
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sender name <span className="text-gray-400 font-normal">(shown to recipients)</span>
              </label>
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder={senderNameDefault || 'Your name or company'}
                maxLength={128}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Prefilled from your profile. Edit to send this envelope under a different name
                (e.g. your company instead of your personal name).
              </p>
              <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={saveSenderAsDefault}
                  onChange={(e) => setSaveSenderAsDefault(e.target.checked)}
                  disabled={!senderName.trim()}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50"
                />
                <span className={!senderName.trim() ? 'text-gray-400' : ''}>
                  Save as default for future envelopes
                </span>
              </label>
            </div>

            {/* Subject + message + expiry */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message to signers{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Add a personal message..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration date
                </label>
                <input
                  type="date"
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Default: 30 days from today. Signers get last-chance emails 72h and 24h before.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remind every
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={14}
                      value={reminderInterval}
                      onChange={(e) => setReminderInterval(e.target.value)}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-600">days</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Up to
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={5}
                      value={reminderMax}
                      onChange={(e) => setReminderMax(e.target.value)}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-600">reminders</span>
                  </div>
                </div>
              </div>
              <p className="-mt-2 text-xs text-gray-400">
                Set reminders to 0 to disable automatic nudges.
              </p>
            </div>

            {sendError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {sendError}
              </div>
            )}

            {/* Zero-field signer banner — only reachable when a signer is
                added after the step-3 gate already passed (Back → add signer
                → Next). Server rejects this envelope anyway, so surface it
                inline with fix-in-place actions. Send stays enabled so the
                server remains the source of truth. */}
            {signersWithoutFields.length > 0 && (() => {
              const blocker = signersWithoutFields[0];
              const blockerLabel = blocker.name || blocker.email || 'Unnamed';
              const canRemoveBlocker = coloredRecipients.length > 1;
              return (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">
                        Signer &ldquo;{blockerLabel}&rdquo; needs at least one required field.
                      </p>
                      <p className="text-xs text-red-600 mt-0.5">
                        Go back to Place Fields or remove them.
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveRecipientId(blocker.id);
                            setStep(2);
                          }}
                          className="text-xs font-medium text-red-700 hover:text-red-900 underline"
                        >
                          Back to fields
                        </button>
                        {canRemoveBlocker && (
                          <button
                            type="button"
                            onClick={() => handleRemoveRecipient(blocker.id)}
                            className="text-xs font-medium text-red-700 hover:text-red-900 underline"
                          >
                            Remove {blockerLabel}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <button
              onClick={handleSend}
              disabled={sending}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send envelope
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Bottom navigation bar (not shown in Step 3 field placement, it uses its own layout) */}
      {step !== 2 && (
        <div className="bg-white border-t border-gray-200 px-6 py-4 shrink-0">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            {step < 3 && (
              <button
                onClick={() => setStep((s) => Math.min(3, s + 1))}
                disabled={!canNext}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Step 3 has its own back/next buttons below the PDF area. Next is
          gated when any signer has zero required fields — mirrors the server
          rule from api/routes/envelopes.js so senders see the block in-place
          instead of post-send. Back is always free. */}
      {step === 2 && (() => {
        const nextBlocked = signersWithoutFields.length > 0;
        const caption = nextBlocked
          ? captionForZeroFieldSigners(signersWithoutFields)
          : `${fields.length} field${fields.length !== 1 ? 's' : ''} placed`;
        const tooltip = nextBlocked ? tooltipForZeroFieldSigners(signersWithoutFields) : undefined;
        return (
          <div className="bg-white border-t border-gray-200 px-6 py-3 shrink-0 flex items-center justify-between">
            <button
              onClick={() => setStep(1)}
              className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <div
              className={`text-xs flex items-center gap-1.5 min-w-0 px-2 ${nextBlocked ? 'text-amber-700 font-medium' : 'text-gray-400'}`}
              title={tooltip}
            >
              {nextBlocked && <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
              <span className="truncate">{caption}</span>
            </div>
            <button
              onClick={() => { if (!nextBlocked) setStep(3); }}
              disabled={nextBlocked}
              aria-disabled={nextBlocked}
              title={tooltip}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        );
      })()}
    </div>
  );
}
